/**
 * AWENI — Main Entry Point
 *
 * Bootstraps the app: initializes context providers, mounts the shell,
 * registers the router, sets up service workers, runs onboarding,
 * and tracks performance metrics.
 */

import './styles/globals.css';
import './styles/themes.css';
import { AuthProvider } from './context/AuthProvider';
import { ThemeProvider } from './context/ThemeProvider';
import { setContext } from './context/AweniContext';
import type { AweniContext, I18nState, NotificationState, OfflineState } from './context/AweniContext';
import { createRouter } from './router';
import { mountApp } from './App';
import { isOnboarded, mountOnboarding } from './shell/Onboarding';
import { NativeService } from './services/NativeService';

const API_BASE = '/identity';

// ---------- Performance Monitoring ----------

const perfMarks = {
  bootstrapStart: 'aweni:bootstrap-start',
  bootstrapEnd: 'aweni:bootstrap-end',
  shellRendered: 'aweni:shell-rendered',
  i18nLoaded: 'aweni:i18n-loaded',
  moduleLoaded: 'aweni:module-loaded',
};

function markPerf(name: string): void {
  try {
    performance.mark(name);
  } catch { /* Performance API not available */ }
}

function measurePerf(name: string, startMark: string, endMark: string): void {
  try {
    performance.measure(name, startMark, endMark);
    const entries = performance.getEntriesByName(name);
    if (entries.length > 0) {
      const duration = entries[entries.length - 1].duration;
      console.log(`[AWENI perf] ${name}: ${duration.toFixed(1)}ms`);
    }
  } catch { /* Performance API not available */ }
}

/** Log all key performance metrics after app is interactive. */
function logPerformanceSummary(): void {
  try {
    // Shell render time: DOMContentLoaded to first paint
    const navEntries = performance.getEntriesByType('navigation') as PerformanceNavigationTiming[];
    if (navEntries.length > 0) {
      const nav = navEntries[0];
      console.log(`[AWENI perf] DOM Content Loaded: ${nav.domContentLoadedEventEnd.toFixed(1)}ms`);
      console.log(`[AWENI perf] DOM Complete: ${nav.domComplete.toFixed(1)}ms`);
    }

    // Paint timing
    const paintEntries = performance.getEntriesByType('paint');
    for (const entry of paintEntries) {
      console.log(`[AWENI perf] ${entry.name}: ${entry.startTime.toFixed(1)}ms`);
    }

    // Bootstrap duration
    measurePerf('aweni:bootstrap-duration', perfMarks.bootstrapStart, perfMarks.bootstrapEnd);

    // i18n load time
    measurePerf('aweni:i18n-duration', perfMarks.bootstrapStart, perfMarks.i18nLoaded);
  } catch { /* silent */ }
}

// ---------- i18n ----------

/** Simple i18n stub — loads JSON translations from /src/i18n/ */
function createI18n(): I18nState {
  const supportedLocales = ['en', 'fr', 'ar', 'es', 'de'];
  let locale = localStorage.getItem('aweni_locale') || navigator.language.slice(0, 2);
  if (!supportedLocales.includes(locale)) locale = 'en';

  const translations: Record<string, Record<string, string>> = {};
  let current: Record<string, string> = {};

  const dir = (): 'ltr' | 'rtl' => locale === 'ar' ? 'rtl' : 'ltr';

  const applyDir = () => {
    document.documentElement.setAttribute('dir', dir());
    document.documentElement.setAttribute('lang', locale);
  };

  const loadLocale = async (loc: string) => {
    if (!translations[loc]) {
      try {
        const mod = await import(`./i18n/${loc}.json`);
        translations[loc] = mod.default || mod;
      } catch {
        translations[loc] = {};
      }
    }
    current = translations[loc] || {};
    markPerf(perfMarks.i18nLoaded);
  };

  // Eager load current locale
  loadLocale(locale);
  applyDir();

  return {
    locale,
    dir: dir(),
    supportedLocales,
    t(key: string): string {
      return current[key] || key;
    },
    setLocale(newLocale: string) {
      if (!supportedLocales.includes(newLocale)) return;
      locale = newLocale;
      localStorage.setItem('aweni_locale', locale);
      loadLocale(locale).then(() => {
        this.locale = locale;
        this.dir = dir();
        applyDir();
      });
    },
  };
}

// ---------- Notifications ----------

/** Notification state stub — fetches from platform API */
function createNotifications(getToken: () => string | null): NotificationState {
  const state: NotificationState = {
    items: [],
    unreadCount: 0,

    async fetch() {
      const token = getToken();
      if (!token) return;
      try {
        const res = await fetch('/api/notifications', {
          headers: { Authorization: `Bearer ${token}` },
          credentials: 'include',
        });
        if (res.ok) {
          const data = await res.json();
          state.items = data.notifications || [];
          state.unreadCount = state.items.filter(n => !n.read).length;
        }
      } catch {
        // Offline or server error — keep existing items
      }
    },

    async markAllRead() {
      const token = getToken();
      if (!token) return;
      try {
        await fetch('/api/notifications/mark-read', {
          method: 'POST',
          headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
          credentials: 'include',
        });
        state.items.forEach(n => (n.read = true));
        state.unreadCount = 0;
      } catch { /* silent */ }
    },

    async markRead(id: string) {
      const token = getToken();
      if (!token) return;
      try {
        await fetch('/api/notifications/mark-read', {
          method: 'POST',
          headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
          body: JSON.stringify({ ids: [id] }),
          credentials: 'include',
        });
        const item = state.items.find(n => n.id === id);
        if (item) {
          item.read = true;
          state.unreadCount = Math.max(0, state.unreadCount - 1);
        }
      } catch { /* silent */ }
    },
  };
  return state;
}

// ---------- Offline ----------

/** Offline connectivity state */
function createOfflineState(): OfflineState {
  const cbs: Array<(online: boolean) => void> = [];
  const state: OfflineState = {
    isOnline: navigator.onLine,
    onStatusChange(cb) { cbs.push(cb); },
  };

  window.addEventListener('online', () => {
    state.isOnline = true;
    cbs.forEach(cb => cb(true));
  });
  window.addEventListener('offline', () => {
    state.isOnline = false;
    cbs.forEach(cb => cb(false));
  });

  return state;
}

// ---------- Activity Tracking ----------

/** Activity tracking — fire-and-forget POST */
function trackActivity(token: string | null, action: string, data?: Record<string, unknown>) {
  if (!token) return;
  fetch('/api/activity/track', {
    method: 'POST',
    headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
    body: JSON.stringify({ app: 'aweni', action, ...data }),
    credentials: 'include',
  }).catch(() => { /* fire-and-forget */ });
}

// ---------- Accessibility: aria-live route announcer ----------

function createRouteAnnouncer(): HTMLElement {
  const announcer = document.createElement('div');
  announcer.id = 'aweni-route-announcer';
  announcer.setAttribute('role', 'status');
  announcer.setAttribute('aria-live', 'polite');
  announcer.setAttribute('aria-atomic', 'true');
  announcer.style.cssText = 'position:absolute;width:1px;height:1px;overflow:hidden;clip:rect(0,0,0,0);white-space:nowrap;';
  document.body.appendChild(announcer);
  return announcer;
}

// ---------- Bootstrap ----------

/** Bootstrap the AWENI application */
async function bootstrap() {
  markPerf(perfMarks.bootstrapStart);

  const app = document.getElementById('app');
  if (!app) throw new Error('Missing #app element');

  // Initialize providers
  const auth = new AuthProvider();
  const theme = new ThemeProvider();
  const i18n = createI18n();
  const notifications = createNotifications(() => auth.getToken());
  const offline = createOfflineState();

  // Create router
  const router = createRouter();

  // Create route announcer for screen readers
  const routeAnnouncer = createRouteAnnouncer();

  // Build context
  const ctx: AweniContext = {
    auth,
    i18n,
    theme,
    notifications,
    offline,
    navigate: (path: string) => router.navigate(path),
    trackActivity: (action: string, data?: Record<string, unknown>) =>
      trackActivity(auth.getToken(), action, data),
    apiBase: API_BASE,
  };

  setContext(ctx);

  // ---------- Native platform initialization ----------
  if (NativeService.isNative()) {
    // Set status bar to match dark theme
    NativeService.setStatusBarStyle(theme.isDark ? 'dark' : 'light');
    NativeService.setStatusBarColor('#0F172A');

    // Sync status bar on theme changes
    theme.onChange((isDark) => {
      NativeService.setStatusBarStyle(isDark ? 'dark' : 'light');
      NativeService.setStatusBarColor(isDark ? '#0F172A' : '#FFFFFF');
    });
  }

  // Android back button: navigate back or minimize app
  NativeService.onBackButton(() => {
    if (window.history.length > 1) {
      window.history.back();
    }
    // If at root, Android default behavior (minimize) applies
  });

  // Pause/resume: refresh notifications on resume
  NativeService.onAppStateChange((isActive) => {
    if (isActive && auth.isAuthenticated) {
      notifications.fetch();
    }
  });

  // Show onboarding if first time
  if (!isOnboarded()) {
    await mountOnboarding(app, ctx);
  }

  // Mount the app shell
  mountApp(app, ctx, router);

  markPerf(perfMarks.shellRendered);

  // Announce route changes to screen readers
  router.onChange((route: { module: string }) => {
    const moduleKey = `nav_${route.module}`;
    const translated = i18n.t(moduleKey);
    const label = translated !== moduleKey ? translated : route.module;
    routeAnnouncer.textContent = label;

    // Track module load time
    markPerf(perfMarks.moduleLoaded);
    measurePerf(`aweni:module-switch`, perfMarks.shellRendered, perfMarks.moduleLoaded);
  });

  // Load notifications in the background
  if (auth.isAuthenticated) {
    notifications.fetch();
  }

  // Register service worker
  if ('serviceWorker' in navigator) {
    try {
      await navigator.serviceWorker.register('/aweni/sw.js', { scope: '/aweni/' });
    } catch {
      // SW registration failed — app still works without it
    }
  }

  // PWA install prompt — managed by InstallManager
  const { getInstallManager } = await import('./services/InstallManager');
  const installMgr = getInstallManager();

  // Show celebration toast when installed
  installMgr.onInstalled(() => {
    installMgr.showCelebration();
  });

  // Show install banner on 2nd+ visit, if not installed and not dismissed
  if (!installMgr.isInstalled() && installMgr.hasVisitedAtLeast(2) && !installMgr.isDismissed()) {
    // Wait a moment for the page to settle, then show banner
    setTimeout(() => {
      if (installMgr.isInstalled() || installMgr.isDismissed()) return;

      const banner = document.createElement('div');
      banner.id = 'aweni-install-banner';
      banner.setAttribute('role', 'alert');
      banner.style.cssText = `
        position: fixed;
        bottom: 64px;
        left: 12px;
        right: 12px;
        z-index: 90;
        background: var(--card-bg, #1E293B);
        border: 1px solid var(--border, rgba(255,255,255,0.1));
        border-radius: 12px;
        padding: 12px 16px;
        display: flex;
        align-items: center;
        gap: 12px;
        box-shadow: 0 4px 24px rgba(0,0,0,0.3);
        animation: slideUp 300ms ease-out;
      `;

      const text = document.createElement('span');
      text.style.cssText = 'flex:1;font-size:13px;color:var(--text-primary, #F1F5F9);';
      text.textContent = installMgr.isIOS()
        ? (i18n.t('install_ios_prompt') !== 'install_ios_prompt' ? i18n.t('install_ios_prompt') : 'Add AWENI to your Home Screen')
        : (i18n.t('install_prompt') !== 'install_prompt' ? i18n.t('install_prompt') : 'Get the full experience — Install AWENI');

      const installBtn = document.createElement('button');
      installBtn.style.cssText = 'padding:6px 16px;border-radius:8px;background:var(--accent, #10B981);color:#fff;font-size:13px;font-weight:600;white-space:nowrap;min-height:44px;border:none;cursor:pointer;';
      installBtn.textContent = installMgr.isIOS() ? 'How?' : 'Install';
      installBtn.setAttribute('aria-label', text.textContent);
      installBtn.addEventListener('click', async () => {
        if (installMgr.isIOS()) {
          // Redirect to download page which has the iOS guide
          window.location.href = '/aweni/download/';
        } else if (installMgr.isInstallable()) {
          const accepted = await installMgr.promptInstall();
          if (accepted) {
            installMgr.showCelebration();
          }
        } else {
          window.location.href = '/aweni/download/';
        }
        banner.remove();
      });

      const dismissBtn = document.createElement('button');
      dismissBtn.style.cssText = 'width:44px;height:44px;display:flex;align-items:center;justify-content:center;border-radius:50%;flex-shrink:0;border:none;background:transparent;cursor:pointer;color:var(--text-secondary, #94A3B8);';
      dismissBtn.setAttribute('aria-label', i18n.t('common_close') !== 'common_close' ? i18n.t('common_close') : 'Close');
      dismissBtn.innerHTML = '<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>';
      dismissBtn.addEventListener('click', () => {
        installMgr.dismiss();
        banner.remove();
      });

      banner.append(text, installBtn, dismissBtn);
      document.body.appendChild(banner);

      // Auto-dismiss after 20 seconds (but remember dismissal)
      setTimeout(() => {
        if (banner.parentNode) banner.remove();
      }, 20000);
    }, 3000); // Show after 3 seconds
  }

  markPerf(perfMarks.bootstrapEnd);

  // Log performance summary after everything is ready
  if (typeof requestIdleCallback === 'function') {
    requestIdleCallback(() => logPerformanceSummary());
  } else {
    setTimeout(() => logPerformanceSummary(), 1000);
  }
}

bootstrap();
